import React, { ReactNode, useMemo } from 'react'
import { withRouter } from 'react-router-dom'

import { useEditor } from '@toasttab/sites-components'

import { RequestContextProps } from 'src/lib/js/context'
import { isToastOrderRequest } from 'src/public/js/siteUtilities'
import { t } from 'src/translations'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { Locations } from './location/Locations'
import { Fides } from 'src/shared/js/external/fides'
import { useCookieConsentContext } from 'src/public/js/cookie_consent/CookieConsentContext'
import { PoweredByToast } from 'public/components/default_template/footer/PoweredByToast'
import { useWebsiteLayoutSpacing } from 'public/components/default_template/useWebsiteLayoutSpacing'

declare global {
  interface Window {
    Fides: Fides | undefined
  }
}

type FooterProps = { children?: ReactNode }

type WrappedFooterProps = FooterProps &
  Pick<RequestContextProps, 'staticContext'>

const WrappedFooter = ({ children, staticContext }: WrappedFooterProps) => {
  const { restaurant } = useRestaurant()
  const { useEditableRef } = useEditor()
  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'footer',
    displayName: t('footer.footer'),
    actions: [],
    schema: { fields: [] }
  })
  const { ethycaEnabled } = useCookieConsentContext()
  const websiteLayoutSpacing = useWebsiteLayoutSpacing()
  const styles = useMemo(() => {
    const styles: React.CSSProperties = {}
    if (!restaurant?.theme?.enabled) {
      const nonThemedColors = restaurant?.content?.footerConfig?.colors
      if (nonThemedColors?.background) {
        styles.backgroundColor = nonThemedColors.background
      }
      if (nonThemedColors?.text) {
        styles.color = nonThemedColors.text
      }
    }
    return styles
  }, [restaurant])

  if (!restaurant) {
    return null
  }

  return (
    <>
      <div
        role='region'
        className='templateFooter'
        id='footer'
        ref={editableRef}
        style={{ ...styles, ...websiteLayoutSpacing }}
      >
        <div className='footerContent'>
          <Locations />
          <div className='legal-container' role='contentinfo'>
            <PoweredByToast />
            <ul className='legal'>
              <li>
                <a
                  href='https://pos.toasttab.com/terms-of-service/#diner-tos'
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {t('footer.terms-of-service')}
                </a>
              </li>
              <li>
                <span>|</span>
                <a
                  href='https://pos.toasttab.com/privacy'
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {t('footer.privacy-statement')}
                </a>
              </li>
              {isToastOrderRequest(staticContext) && (
                <li>
                  <span>|</span>
                  <a
                    href='https://pos.toasttab.com/privacy#addendum-a'
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    {t('footer.ca-privacy-statement')}
                  </a>
                </li>
              )}
              {ethycaEnabled && (
                <>
                  <li
                    data-testid='ethyca-cookie-link'
                    className='ethyca-cookie-link'
                  >
                    <span>|</span>
                    <a
                      href='#'
                      onClick={(e) => {
                        e.preventDefault()
                        window?.Fides?.showModal()
                      }}
                    >
                      {window?.Fides?.getModalLinkLabel?.() ||
                        t('footer.cookie-settings')}
                    </a>
                  </li>
                </>
              )}
            </ul>
          </div>
          {children}
        </div>
      </div>
    </>
  )
}

const FooterWithRouter = withRouter<
  FooterProps & RequestContextProps,
  React.ComponentType<FooterProps & RequestContextProps>
>(WrappedFooter)

const Footer = (props: FooterProps) => {
  const { isEditor } = useEditor()

  if (isEditor) {
    return <WrappedFooter {...props} />
  }

  return <FooterWithRouter {...props} />
}

export default Footer
